import { Fragment, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "../../commons/Button/Button";
import { Form, FormLoader, Input } from "../../commons/Form/Form";
import { PersonRemove } from "@mui/icons-material";
import {
  Setting,
  SettingDisplay,
  SettingModder,
  SettingList,
} from "../Common/Settings";
import api from "../../../api/resources";
import authAPI from "../../../api/authentication";
import { i18n } from "../../../i18n";
import { formatDate } from "../../../format";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { useNavigate } from "react-router-dom";
import routes from "../../../routes/routes";

export default function Users() {
  const { t } = useTranslation(["common", "settings"]);
  document.title = t("settings:tabs.users.title");
  const dispatchModal = useModalDispatch();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatchToast = useToastsDispatch();

  const init = async () => {
    const companyRes = await api.getActiveCompany();
    if (companyRes.ok) {
      const companyJsonRes = await companyRes.json();
      const userResponse = await authAPI.getUserInfo();
      if (userResponse.status === 200) {
        const userData = userResponse.data.user;
        setCurrentUser(userData);

        const managersList = companyJsonRes.managers.map((manager) => ({
          id: manager.id,
          name:
            userData.email === manager.email ? t("common:You") : manager.name,
          email: manager.email,
          status:
            userData.is_admin && userData.email === manager.email
              ? "admin"
              : "active", // admin status will show up as active for a current user that is not admin
        }));

        let invitedList = [];
        const invitationsRes = await api.getInvitations(companyJsonRes.id);
        if (invitationsRes.ok) {
          const invitationsJsonRes = await invitationsRes.json();
          if (invitationsJsonRes) {
            invitedList = invitationsJsonRes.map((invitation) => ({
              id: invitation.email,
              name: invitation.email,
              email: invitation.email,
              status: "pending",
              date: invitation.sent,
              invitationKey: invitation.key,
            }));
          }
        }

        setUsers([...managersList, ...invitedList]);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    init();
  }, []);

  const handleDelete = (user) => {
    const handleDeleteManager = async (userID) => {
      const response = await api.deleteManager(userID);

      if (!response.ok) {
        dispatchModal({ type: "success" });
        dispatchToast({
          type: "add",
          variant: "info",
          subheading: t("common:toasts.errorToast.subheading"),
        });
        return;
      }

      dispatchModal({ type: "success" });
    };

    const handleDeleteInvitation = async (invitationKey) => {
      const response = await api.deleteInvitation(invitationKey);

      if (!response.ok) {
        dispatchModal({ type: "success" });
        dispatchToast({
          type: "add",
          variant: "info",
          subheading: t("common:toasts.errorToast.subheading"),
        });
        return;
      }

      dispatchModal({ type: "success" });
    };

    dispatchModal({
      type: "add",
      title: t("settings:modals.deleteManager.title"),
      message:
        user.status === "active" ? (
          <Trans
            t={t}
            i18nKey="settings:modals.deleteManager.message"
            values={{ name: user.name }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey="settings:modals.deleteInvite.message"
            values={{ email: user.email }}
          />
        ),
      component: (
        <Form
          button={t("common:delete")}
          onSubmit={
            user.status === "active"
              ? () => handleDeleteManager(user.id)
              : () => handleDeleteInvitation(user.invitationKey)
          }
        />
      ),
      onSuccess: init,
    });
  };

  const handleInvite = () => {
    dispatchModal({
      type: "add",
      title: t("settings:modals.inviteUser.title"),
      message: t("settings:modals.inviteUser.message"),
      component: <UserInviteForm />,
      onSuccess: init,
    });
  };

  const handleSwitch = () => {
    dispatchModal({
      type: "add",
      title: t("settings:modals.switchAdmin.title"),
      message: t("settings:modals.switchAdmin.message"),
      component: (
        <AdminSwitchForm
          nonAdminManagers={users.filter((user) => user.status === "active")}
        />
      ),
      onSuccess: init,
    });
  };

  const pillColors = {
    active: "green",
    pending: "yellow",
  };

  if (loading) return <FormLoader />;
  return (
    <div>
      <SettingList>
        {users
          .sort((user) => (user.status === "admin" ? -1 : 1)) // admin first
          .map((user) => (
            <Setting key={user.email} testid={user.status}>
              <SettingDisplay
                testid={user.status}
                label={user.name}
                tag={t(`common:${user.status}`)}
                tagColor={pillColors[user.status]}
                value={
                  user.status === "pending" ? (
                    <Trans
                      t={t}
                      i18nKey={"settings:tabs.users.items.sent"}
                      values={{
                        date: formatDate(user.date, i18n.resolvedLanguage),
                      }}
                      context={
                        ["01", "08", "11"].includes(user.date.substring(8, 10))
                          ? "elided"
                          : ""
                      }
                    />
                  ) : (
                    user.email
                  )
                }
              />
              {currentUser.is_admin && user.status !== "admin" && (
                <SettingModder
                  action="delete"
                  icon={<PersonRemove />}
                  onClick={() => handleDelete(user)}
                />
              )}
            </Setting>
          ))}
      </SettingList>
      {currentUser.is_admin && (
        <div
          style={{
            marginTop: "1.5rem",
            display: "flex",
            gap: "1rem",
          }}
        >
          <Fragment>
            {users.filter((user) => user.status === "active").length > 0 && (
              <Button
                id={"admin-switch"}
                variant="primary_a"
                text={t("settings:tabs.users.buttons.switchAdmin")}
                onClick={handleSwitch}
              />
            )}
            <Button
              id="user-invite"
              variant="primary_a"
              text={t("settings:tabs.users.buttons.addUser")}
              onClick={handleInvite}
            />
          </Fragment>
        </div>
      )}
    </div>
  );
}

function UserInviteForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const handleSubmit = async () => {
    const response = await api.inviteUser({ email });

    if (!response.ok) {
      dispatchModal({ type: "success" });
      dispatchToast({
        type: "add",
        variant: "info",
        subheading: t("common:toasts.errorToast.subheading"),
      });
      return;
    }

    dispatchModal({ type: "success" });
  };

  const emailInput = {
    name: "email",
    type: "email",
    label: t("common:email"),
    value: email,
    onChange: setEmail,
  };

  return (
    <Form button={t("common:invite")} onSubmit={handleSubmit}>
      <Input {...emailInput} />
    </Form>
  );
}

function AdminSwitchForm({ nonAdminManagers }) {
  const { t } = useTranslation(["common", "settings"]);
  const [admin, setAdmin] = useState(null);
  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();
  const navigate = useNavigate();

  const options = nonAdminManagers.map((manager) => ({
    label: manager.name,
    value: manager.id,
  }));

  const input = {
    name: "admin",
    type: "radio",
    label: t("settings:modals.switchAdmin.label"),
    options,
    value: admin,
    onChange: setAdmin,
  };

  const handleSubmit = async () => {
    const response = await api.updateActiveCompany({ admin: admin.value });

    if (!response.ok) {
      dispatchModal({ type: "success" });
      dispatchToast({
        type: "add",
        variant: "info",
        subheading: t("common:toasts.errorToast.subheading"),
      });
      return;
    }
    navigate(routes.Settings.profile);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Input {...input} />
    </Form>
  );
}
